var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getSignature, uploadToCloudinary } from '@/api/cloudinary';
import { BASE_URL, CURRENT_PROTOCOL } from '@/api/realApi/_constants';
import CropperModal from '@/components/CropperModal';
import { useApiContext } from '@/context/ApiContext';
export default function UnifiedCloudinaryForm({ onUploaded, defaultUrl = '', defaultFile = null, cropOutputType = 'image/jpeg', cropQuality = 0.9, maxFileMB = 3, useProxyForUrlCrop = true, uploadPreset = 'avatars', }) {
    const { t } = useTranslation();
    const { useMockApi } = useApiContext();
    const fileInputRef = useRef(null);
    const [resultUrl, setResultUrl] = useState(defaultUrl);
    const [urlInput, setUrlInput] = useState(defaultUrl);
    const [fileInput, setFileInput] = useState(null);
    const [preparedFile, setPreparedFile] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(defaultUrl || '');
    const [cropOpen, setCropOpen] = useState(false);
    const [cropSrc, setCropSrc] = useState('');
    const [revokeOnClose, setRevokeOnClose] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const proxySrc = useMemo(() => {
        if (!urlInput.trim())
            return '';
        return `${CURRENT_PROTOCOL}://${BASE_URL}/api/core/image_proxy/?url=${encodeURIComponent(urlInput.trim())}`;
    }, [urlInput]);
    // Если defaultUrl изменился снаружи — подставить его в urlInput и previewSrc
    useEffect(() => {
        if (defaultUrl) {
            setUrlInput(defaultUrl);
            setPreviewSrc(defaultUrl);
            setFileInput(null);
            fileInputRef.current && (fileInputRef.current.value = '');
        }
    }, [defaultUrl]);
    // Если передан defaultFile — установить его как исходный файл и превью
    useEffect(() => {
        if (defaultFile) {
            setFileInput(defaultFile);
            setPreparedFile(null);
            setUrlInput('');
            fileInputRef.current && (fileInputRef.current.value = '');
            setPreviewFromFile(defaultFile);
        }
    }, [defaultFile]);
    const isDefaultImage = useMemo(() => {
        const isResUrl = resultUrl === urlInput.trim();
        const isDefaultUrl = defaultUrl === urlInput.trim();
        return (isResUrl || isDefaultUrl) && !preparedFile && !fileInput;
    }, [resultUrl, urlInput, defaultUrl, preparedFile, fileInput]);
    const reset = () => {
        setPreparedFile(null);
        setResult(null);
        setError('');
        setProgress(0);
    };
    const setPreviewFromFile = (f) => {
        if (!f) {
            setPreviewSrc('');
            return;
        }
        const url = URL.createObjectURL(f);
        setPreviewSrc(url);
    };
    const onChangeUrl = (e) => {
        reset();
        setUrlInput(e.target.value);
        setFileInput(null);
        fileInputRef.current && (fileInputRef.current.value = '');
        setPreviewSrc(e.target.value);
    };
    const onChangeFile = (e) => {
        var _a, _b;
        reset();
        const f = (_b = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
        setFileInput(f);
        setUrlInput('');
        setPreviewFromFile(f);
    };
    const openCropForUrl = () => {
        const u = urlInput.trim();
        if (!u)
            return;
        setError('');
        const src = useProxyForUrlCrop ? proxySrc || u : u;
        setCropSrc(src);
        setRevokeOnClose(false);
        setCropOpen(true);
    };
    const openCropForFile = () => {
        if (!fileInput)
            return;
        setError('');
        const blobUrl = URL.createObjectURL(fileInput);
        setCropSrc(blobUrl);
        setRevokeOnClose(true);
        setCropOpen(true);
    };
    const closeCrop = () => {
        if (revokeOnClose && cropSrc) {
            try {
                URL.revokeObjectURL(cropSrc);
            }
            catch (_a) {
                console.warn('Error on revoke URL:', cropSrc);
            }
        }
        setCropOpen(false);
        setCropSrc('');
        setRevokeOnClose(false);
    };
    const onCropped = (f) => {
        setPreparedFile(f);
        setPreviewFromFile(f);
    };
    const ensureSize = (f) => {
        const mb = f.size / (1024 * 1024);
        if (mb > maxFileMB)
            throw new Error(`File is too large: ${mb.toFixed(2)} MB (limit ${maxFileMB} MB)`);
    };
    const onSubmit = (e) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        e.preventDefault();
        e.stopPropagation();
        setError('');
        setResult(null);
        setProgress(0);
        const hasPrepared = !!preparedFile;
        const hasFile = !!fileInput;
        const hasUrl = !!urlInput.trim();
        if (!hasPrepared && !hasFile && !hasUrl) {
            setError(t('upload.images.emptyInput'));
            return;
        }
        if (useMockApi) {
            if (onUploaded) {
                onUploaded({
                    asset_folder: 'mock_folder',
                    asset_id: 'mock_id',
                    bytes: 0,
                    created_at: new Date().toISOString(),
                    display_name: 'mock_id',
                    format: 'jpg',
                    height: 0,
                    original_filename: 'mock_image.jpg',
                    public_id: 'mock_id',
                    secure_url: urlInput,
                    url: urlInput,
                    width: 0,
                });
            }
            return;
        }
        try {
            setUploading(true);
            let payload;
            if (hasPrepared && preparedFile) {
                ensureSize(preparedFile);
                payload = preparedFile;
            }
            else if (hasFile && fileInput) {
                ensureSize(fileInput);
                payload = fileInput;
            }
            else {
                try {
                    new URL(urlInput.trim());
                }
                catch (_d) {
                    throw new Error(t('upload.invalidURL'));
                }
                payload = urlInput.trim(); // Cloudinary сам скачает
            }
            const sig = yield getSignature(uploadPreset);
            const data = yield uploadToCloudinary(payload, sig, (evt) => {
                if (!evt.total)
                    return;
                setProgress(Math.round((evt.loaded * 100) / evt.total));
            });
            setResult({ secure_url: data.secure_url, public_id: data.public_id });
            setResultUrl(data.secure_url);
            setPreparedFile(null);
            setFileInput(null);
            if (onUploaded) {
                onUploaded(data);
            }
        }
        catch (err) {
            console.error(err);
            const message = typeof err === 'object' && err !== null
                ? // @ts-expect-error safe optional chaining for possible axios error shape
                    ((_c = (_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.message) || err.message
                : '';
            setError(message || t('upload.error'));
            setResultUrl(urlInput);
        }
        finally {
            setUploading(false);
        }
    });
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "upload-image-form", style: styles.form, children: [previewSrc ? (_jsx("div", { style: styles.previewCol, children: _jsxs("div", { className: "image-preview-container", style: styles.imagePreviewContainer, children: [_jsxs("div", { className: "image-preview-title", style: styles.imagePreviewTitle, children: [t('upload.images.preview'), ":"] }), _jsx("img", { src: previewSrc, alt: "preview", className: "image-preview", style: styles.imagePreview })] }) })) : null, _jsxs("div", { className: "fields", style: previewSrc ? styles.fieldsCol : styles.fieldsFull, children: [_jsxs("div", { style: styles.field, children: [_jsx("span", { style: styles.label, children: t('upload.images.imageURL') }), _jsxs("div", { style: styles.inputRow, children: [_jsx("input", { type: "url", placeholder: "https://example.com/image.jpg", value: urlInput, onChange: onChangeUrl, style: styles.input }), _jsx("button", { type: "button", onClick: openCropForUrl, disabled: !urlInput.trim(), style: styles.button, title: t('upload.images.cropImage'), children: t('upload.images.crop') })] })] }), !useMockApi && (_jsxs("div", { style: styles.field, children: [_jsx("span", { style: styles.label, children: t('upload.images.imageFile') }), _jsxs("div", { style: styles.inputRow, children: [_jsxs("label", { style: styles.fileLabel, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: "image/png,image/jpeg,image/webp", onChange: onChangeFile, style: styles.fileInput, "aria-label": t('upload.images.imageFile') }), _jsx("span", { style: styles.fileButton, children: _jsx("span", { style: styles.fileButtonText, children: fileInput ? fileInput.name : t('upload.selectFile') }) })] }), _jsx("button", { type: "button", onClick: openCropForFile, disabled: !fileInput, style: styles.button, title: t('upload.images.cropImage'), children: t('upload.images.crop') })] }), _jsxs("small", { style: styles.hint, children: [t('upload.images.formats'), ": PNG, JPEG, WEBP \u2022 ", t('upload.limit'), ": ", maxFileMB, "MB"] })] })), _jsxs("div", { className: "buttons-container", style: styles.buttonsContainer, children: [uploading ? (
                                    /* Progress */
                                    _jsxs("div", { style: { marginTop: 12 }, children: [_jsx("div", { style: styles.progressOuter, children: _jsx("div", { style: Object.assign(Object.assign({}, styles.progressInner), { width: `${progress}%` }) }) }), _jsxs("div", { style: { marginTop: 6, fontSize: 12 }, children: [progress, "%"] })] })) : (
                                    /* Submit */
                                    _jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: onSubmit, disabled: uploading || isDefaultImage, style: styles.primaryBtn, children: uploading ? t('upload.uploading') : t('upload.upload') }), onUploaded && !uploading && (
                                            // Кнопка очистки
                                            _jsx("button", { className: "secondary", style: { padding: '4px 12px' }, type: "button", onClick: () => onUploaded(null), disabled: !urlInput, children: t('upload.images.clear') })), result && _jsx("div", { style: styles.successMessage, children: t('upload.success') })] })), error && _jsx("div", { style: { color: 'crimson', marginTop: 6 }, children: error })] })] })] }), createPortal(_jsx(CropperModal, { open: cropOpen, onClose: closeCrop, src: cropSrc || urlInput, outputType: cropOutputType, quality: cropQuality, outName: (fileInput === null || fileInput === void 0 ? void 0 : fileInput.name) || 'webUrlImage', onCropped: (f) => onCropped(f) }), document.body)] }));
}
const styles = {
    form: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
        padding: '18px 10px',
        borderRadius: 8,
        border: '1px solid #dce1e6ff',
        background: '#f1f1f1ff',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.07)',
    },
    previewCol: {
        display: 'inline-flex',
        flexDirection: 'column',
        flex: '0 0 200px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        maxWidth: 220,
        minWidth: 120,
    },
    fieldsCol: {
        display: 'inline-flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 2,
        paddingLeft: 10,
    },
    fieldsFull: {
        display: 'inline-flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'center',
        gap: 2,
    },
    inputRow: {
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    field: {
        marginBottom: 6,
        minWidth: 220,
        width: '100%',
        flex: 2,
    },
    label: {
        display: 'block',
        marginBottom: 2,
        fontWeight: 500,
        fontSize: '0.9rem',
        color: '#444',
    },
    input: {
        flex: 1,
        padding: '4px 8px',
        border: '1px solid #ccc',
        borderRadius: 8,
    },
    fileLabel: {
        display: 'flex',
        width: 'fit-content',
        cursor: 'pointer',
        margin: 0,
        position: 'relative',
        alignItems: 'center',
    },
    fileInput: {
        display: 'none',
    },
    fileButton: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 100,
        maxWidth: 260,
        width: '100%',
        verticalAlign: 'middle',
        padding: '6px 10px',
        borderRadius: 8,
        border: '1px solid #1a73e8',
        background: '#f7faff',
        color: '#1a73e8',
        fontWeight: 500,
        fontSize: '0.9rem',
        transition: 'background 0.15s, color 0.15s',
        cursor: 'pointer',
        overflow: 'hidden',
    },
    fileButtonText: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    imagePreviewContainer: {
        marginTop: 2,
    },
    imagePreviewTitle: {
        fontWeight: 600,
        marginBottom: 6,
    },
    imagePreview: {
        maxWidth: '200px',
        maxHeight: 200,
        borderRadius: 8,
        border: '1px solid #eee',
    },
    button: {
        flexShrink: 0,
        maxWidth: 90,
        width: 'auto',
        padding: '6px 8px',
        borderRadius: 8,
        border: '1px solid #888',
        background: '#fff',
        color: '#333',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    primaryBtn: {
        padding: '4px 26px',
        borderRadius: 8,
        background: '#1a73e8',
        color: '#fff',
        cursor: 'pointer',
    },
    buttonsContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        marginTop: 0,
    },
    hint: {
        fontSize: '0.6rem',
        color: '#0000004b',
        paddingLeft: 2,
    },
    successMessage: {
        fontSize: '0.7rem',
        color: '#4caf50',
        padding: '2px 8px',
        borderRadius: 4,
        background: '#e8f5e9',
        marginLeft: 10,
    },
    progressOuter: {
        height: 10,
        minWidth: 80,
        maxWidth: 120,
        overflow: 'hidden',
    },
    progressInner: {
        height: 10,
        background: '#4a8',
        borderRadius: 6,
        transition: 'width .2s',
    },
};
